import FalconCardHeader from 'components/common/FalconCardHeader';
import IconButton from 'components/common/IconButton';
import SoftBadge from 'components/common/SoftBadge';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import LoadingContainer from 'components/common/LoadingContainer';
import dayjs from 'dayjs';
import { formatMoney } from 'helpers/utils';
import React, { useState, useMemo } from 'react';
import { Button, Card, Col, Row, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrderDetails } from 'hooks/useDashboard';
import { useCreateRefund, useDeleteOrder } from 'hooks/useStripePayments';
import { createDonorFeedback } from 'lib/supabaseQueries';
import { useQueryClient } from '@tanstack/react-query';
import CancellationReasonModal from 'components/common/modals/CancellationReasonModal';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { PDFDownloadLink } from '@react-pdf/renderer';
import InvoicePDF from './InvoicePDF';
import { useDonor } from 'hooks/useDonors';
import { useAuthContext } from 'context/authContext';
import { useLocations } from 'hooks/useDonationSettings';

const DonorGivingDetails = () => {
  const navigate = useNavigate();
  const { id, givingId } = useParams();
  const { order, isLoading, error, refetch } = useOrderDetails(givingId);
  const { createRefundAsync, isRefunding } = useCreateRefund();
  const queryClient = useQueryClient();
  const { deleteOrderAsync, isDeleting } = useDeleteOrder();
  const { donor } = useDonor(id);
  const { organization, isOrganizationAdmin, isAdmin, user } = useAuthContext();
  const { locations } = useLocations();
  // Package "location" is a jsonb array of location ids (not a FK), so resolve
  // them to country names via a lookup map.
  const locationMap = useMemo(
    () =>
      Object.fromEntries((locations || []).map(l => [l.id, l.location_name])),
    [locations]
  );
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleRefund = async (reason, notes) => {
    try {
      await createRefundAsync({
        orderId: givingId,
        reason,
        notes
      });
      // Mirror the cancellation into the donor's Notes tab (same as
      // subscription cancellations) so the history is in one place
      await createDonorFeedback({
        donar_id: id,
        description: [
          `Donation cancelled/refunded (${formatMoney(
            order?.donation_amount ?? 0
          )}, ${dayjs(order?.created_at).format('DD/MM/YYYY')}).`,
          reason ? `Reason: ${reason}.` : null,
          notes ? `Notes: ${notes}` : null
        ]
          .filter(Boolean)
          .join(' '),
        priority: 'Normal',
        user_id: user?.id || null,
        branch_id: order?.branch_id || null
      });
      queryClient.invalidateQueries({ queryKey: ['donorFeedback'] });
      setShowRefundModal(false);
      refetch();
    } catch (err) {
      // Error toast handled by the hook
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOrderAsync({ orderId: givingId });
      setShowDeleteModal(false);
      // The order no longer exists - go back to the giving list.
      navigate(-1);
    } catch (err) {
      // Error toast (incl. "funds a sponsorship") handled by the hook
      setShowDeleteModal(false);
    }
  };

  const staffName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(' ');

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (error || !order) {
    return (
      <Alert variant="danger" className="text-center">
        Failed to load order details. Please try again.
      </Alert>
    );
  }

  const getBadgeColor = status => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'danger';
      case 'refunded':
      case 'partially_refunded':
        return 'info';
      case 'cancelled':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  // const columns = [
  //   {
  //     accessor: 'ref',
  //     Header: 'Ref',
  //     cellProps: {
  //       className: 'py-2'
  //     },
  //     Cell: rowData => {
  //       const { ref } = rowData.row.original;
  //       return <Link to={`/donors/${id}/giving/${ref}`}>{ref}</Link>;
  //     }
  //   },
  //   {
  //     accessor: 'date',
  //     Header: 'Date',
  //     cellProps: {
  //       className: 'py-2'
  //     }
  //   },
  //   {
  //     accessor: 'pay_sys',
  //     Header: 'Pay-sys',
  //     cellProps: {
  //       className: 'py-2'
  //     }
  //   },
  //   {
  //     accessor: 'donation',
  //     Header: 'Donation',
  //     cellProps: {
  //       className: 'py-2'
  //     }
  //   },
  //   {
  //     accessor: 'amount',
  //     Header: 'Amount',
  //     cellProps: {
  //       className: 'py-2'
  //     }
  //   },
  //   {
  //     accessor: 'id',
  //     Header: '',
  //     cellProps: {
  //       className: 'py-2 text-end'
  //     },
  //     disableSortBy: true,

  //     Cell: rowData => {
  //       const { id } = rowData.row;
  //       return (
  //         <TableRowAction
  //           onCLickEdit={() => console.log(id)}
  //           onCLickDelete={() => console.log(id)}
  //         />
  //       );
  //     }
  //   }
  // ];

  // const weSponsorshipData = [
  //   { id: 'sp1', label: 'Ref.', value: givingId },
  //   { id: 'sp2', label: 'Created on', value: '12/10/2022 21:43' },
  //   { id: 'sp3', label: 'First Paid ', value: '12/10/2022 21:43' },
  //   {
  //     id: 'sp4',
  //     label: 'Next rebill',
  //     value: '12/11/2022',
  //     type: 'stop_Recurring'
  //   },
  //   {
  //     id: 'sp5',
  //     label: 'Billing Cycle',
  //     value: 'Recurring payment',
  //     valueClass: 'text-primary'
  //   },
  //   { id: 'sp6', label: 'Method', value: 'Credit Card - XXX42' },
  //   {
  //     id: 'sp7',
  //     label: 'Status',
  //     value: 'Success',
  //     valueClass: 'text-success'
  //   },
  //   { id: 'sp8', label: 'Amount Paid', value: '$1,500.00' },
  //   {
  //     id: 'sp9',
  //     label: 'Remarks',
  //     value: 'No Comments',
  //     type: 'remarks'
  //   }
  // ];

  return (
    <>
      <Card className="mb-3">
        <FalconCardHeader
          title={
            <IconButton
              onClick={() => navigate(-1)}
              icon={'arrow-left'}
              iconClassName="me-2"
              variant="link"
              className="px-0 fw-bold fs--1 fs-md-0"
            >
              Giving / {order.simple_id}
            </IconButton>
          }
          light={false}
          titleTag="div"
          menuClassName="align-items-center"
          endEl={
            <div className="d-flex gap-2">
              <PDFDownloadLink
                document={
                  <InvoicePDF
                    order={order}
                    donor={donor}
                    organization={organization}
                  />
                }
                fileName={`invoice-${order.simple_id}.pdf`}
              >
                {({ loading }) => (
                  <Button size="sm" variant="falcon-default" disabled={loading}>
                    {loading ? 'Generating...' : 'Download Invoice'}
                  </Button>
                )}
              </PDFDownloadLink>
              {(isOrganizationAdmin || isAdmin) &&
                order.payment_status === 'paid' &&
                order.stripe_invoice_id && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => setShowRefundModal(true)}
                  >
                    Refund
                  </Button>
                )}
              {(isOrganizationAdmin || isAdmin) &&
                order.payment_status === 'paid' &&
                !order.stripe_invoice_id && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete
                  </Button>
                )}
            </div>
          }
        />
      </Card>
      {/* <Card className="mb-3">
        <Card.Header className="pb-0">
          <Card.Title className="fs-0 text-700 mb-0">
            {order.simple_id}
            <span className="fw-normal ms-1">- Recurring Sponsorship</span>
          </Card.Title>
        </Card.Header>
        <Card.Body className="pt-0">
          <hr />
          {weSponsorshipData?.map(data => (
            <Flex key={data.id}>
              <p className="donor-info-title fs--1 fs-md-0 text-1000">
                {data.label}
              </p>
              <p className={classNames(data.valueClass, 'fs--1 fs-md-0')}>
                {data.type === 'remarks' && (
                  <FontAwesomeIcon icon={'pencil-alt'} className="me-2" />
                )}{' '}
                {data.value}
              </p>
              {data.type === 'stop_Recurring' && (
                <>
                  <span className="mx-2 fs--1 fs-md-0">|</span>
                  <IconButton
                    size="sm"
                    icon={'stop-circle'}
                    className="bg-soft-danger text-danger fs--1 fs-md-0 rounded-4 border-0"
                    style={{ height: '26px' }}
                  >
                    Stop Recurring
                  </IconButton>
                </>
              )}
            </Flex>
          ))}
          <hr className="mt-0" />
          <Flex justifyContent={'end'}>
            <IconButton
              icon="trash-alt"
              iconClassName={'me-2'}
              variant="danger"
              className="me-2"
              onClick={() =>
                navigate(parseRouteParams(paths.donorGiving, { id }))
              }
            >
              Delete
            </IconButton>
            <Button
              variant="primary"
              onClick={() =>
                navigate(parseRouteParams(paths.donorGiving, { id }))
              }
            >
              Issue Refund
            </Button>
          </Flex>
        </Card.Body>
      </Card> */}
      {/* <Card className="mb-3">
        <Row>
          <Col md={12} xxl={12} className="h-md-100">
            {weDonorInvoiceData && (
              <CustomAdvanceTable
                data={weDonorInvoiceData}
                columns={columns}
                title="Related Payments"
                addNew={false}
              />
            )}
          </Col>
        </Row>
      </Card> */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Order Details</h5>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <div className="d-flex flex-column gap-2">
                <div>
                  <small className="text-muted">Order ID</small>
                  <p className="mb-0">{order.simple_id}</p>
                </div>
                <div>
                  <small className="text-muted">Status</small>
                  <div>
                    <SoftBadge pill bg={getBadgeColor(order.payment_status)}>
                      {order.payment_status?.charAt(0).toUpperCase() +
                        order.payment_status?.slice(1)}
                    </SoftBadge>
                  </div>
                </div>
                <div>
                  <small className="text-muted">Created At</small>
                  <p className="mb-0">
                    {dayjs(order.created_at).format('DD MMM YYYY, HH:mm')}
                  </p>
                </div>
                <div>
                  <small className="text-muted">Payment Method</small>
                  <div>
                    {order.payment_method?.payment_name ? (
                      <SoftBadge
                        pill
                        bg={
                          order.payment_method.payment_name === 'Cash'
                            ? 'success'
                            : 'primary'
                        }
                      >
                        {order.payment_method.payment_name}
                      </SoftBadge>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                </div>
                {order.stripe_invoice_id && (
                  <div>
                    <small className="text-muted">Invoice ID</small>
                    <p className="mb-0">{order.stripe_invoice_id}</p>
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-column gap-2">
                <div>
                  <small className="text-muted">Subtotal</small>
                  <p className="mb-0">{formatMoney(order.subtotal)}</p>
                </div>
                <div>
                  <small className="text-muted">Transaction Fee</small>
                  <p className="mb-0">{formatMoney(order.transaction_fees)}</p>
                </div>
                <div>
                  <small className="text-muted">Total Amount</small>
                  <p className="mb-0 fw-bold">
                    {formatMoney(order.donation_amount)}
                  </p>
                </div>
                <div>
                  <small className="text-muted">Gift Aid</small>
                  <p className="mb-0">
                    {order.gift_aid_eligible ? 'Eligible' : 'Not Eligible'}
                  </p>
                </div>
                {order.refunds?.length > 0 && (
                  <div>
                    <small className="text-muted">Total Refunded</small>
                    <p className="mb-0 text-danger fw-bold">
                      {formatMoney(
                        order.refunds.reduce((sum, r) => sum + r.amount, 0)
                      )}
                    </p>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <div className="mt-4">
            <h6>Order Items</h6>
            <div className="d-flex flex-column gap-3">
              {order.order_details.map(item => {
                // Prefer the location(s) chosen for THIS donation; only fall
                // back to the package's full country list for legacy rows
                // saved before the per-donation location existed.
                const chosenLocationIds = item.location_id_list?.length
                  ? item.location_id_list
                  : item.package?.location || [];
                const appealCountries = chosenLocationIds
                  .map(locId => locationMap[locId])
                  .filter(Boolean);
                const activeSponsorships = (item.sponsorships || []).filter(
                  sp => sp.status === 'active'
                );
                return (
                  <Card key={item.id} className="bg-light">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">
                            {item.package?.package_title || 'Quick Donation'}
                          </h6>
                          {item.donation_type?.donation_type_name && (
                            <small className="text-muted d-block">
                              {item.donation_type.donation_type_name}
                            </small>
                          )}
                          <small className="text-muted d-block">
                            {item.billing_condition}
                          </small>
                          {appealCountries.length > 0 && (
                            <small className="text-muted d-block">
                              Country: {appealCountries.join(', ')}
                            </small>
                          )}
                          {item.package?.simple_id && (
                            <small className="text-muted d-block">
                              ID: {item.package.simple_id}
                            </small>
                          )}
                        </div>
                        <div className="text-end">
                          <h6 className="mb-0">{formatMoney(item.amount)}</h6>
                        </div>
                      </div>

                      {activeSponsorships.length > 0 && (
                        <div className="mt-3 pt-3 border-top">
                          <small className="text-600 fw-semi-bold d-block mb-2">
                            Sponsoring
                          </small>
                          <div className="d-flex flex-column gap-2">
                            {activeSponsorships.map(sp => {
                              const orphan = sp.orphan;
                              if (!orphan) return null;
                              const beneficiaryName = [
                                orphan.first_name,
                                orphan.middle_name,
                                orphan.last_name
                              ]
                                .filter(Boolean)
                                .join(' ');
                              const typeName =
                                sp.beneficiary_sponsorship_type
                                  ?.sponsorship_type?.type;
                              return (
                                <Card
                                  key={sp.id}
                                  className="border shadow-none"
                                >
                                  <Card.Body className="py-2 px-3">
                                    <Flex
                                      alignItems="center"
                                      justifyContent="between"
                                    >
                                      <Flex alignItems="center">
                                        <Avatar
                                          src={orphan.image}
                                          name={beneficiaryName}
                                          size="2xl"
                                          className="me-2"
                                        />
                                        <div>
                                          <p className="mb-0 fw-medium">
                                            {beneficiaryName}
                                            {orphan.simple_id && (
                                              <span className="text-500 fw-normal ms-1">
                                                #{orphan.simple_id}
                                              </span>
                                            )}
                                          </p>
                                          <small className="text-muted d-block">
                                            {[typeName, orphan.country]
                                              .filter(Boolean)
                                              .join(' • ')}
                                          </small>
                                          {sp.coverage_percentage != null && (
                                            <small className="text-muted">
                                              {sp.coverage_percentage}% coverage
                                            </small>
                                          )}
                                        </div>
                                      </Flex>
                                      <SoftBadge
                                        pill
                                        bg={
                                          sp.status === 'active'
                                            ? 'success'
                                            : 'secondary'
                                        }
                                      >
                                        {sp.status}
                                      </SoftBadge>
                                    </Flex>
                                  </Card.Body>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </div>

          {order.refunds?.length > 0 && (
            <div className="mt-4">
              <h6>Refund History</h6>
              <div className="d-flex flex-column gap-2">
                {order.refunds
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                  )
                  .map(refund => {
                    const refundedByName = refund.refunded_by_user
                      ? `${refund.refunded_by_user.first_name || ''} ${
                          refund.refunded_by_user.last_name || ''
                        }`.trim()
                      : null;
                    return (
                      <Card
                        key={refund.id}
                        className="bg-light border-danger border-start border-3"
                      >
                        <Card.Body className="py-2 px-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <span className="text-danger fw-bold">
                                -{formatMoney(refund.amount)}
                              </span>
                              <small className="text-muted ms-2">
                                {dayjs(refund.created_at).format(
                                  'DD MMM YYYY, HH:mm'
                                )}
                              </small>
                              {refundedByName && (
                                <small className="text-muted ms-2">
                                  by {refundedByName}
                                </small>
                              )}
                              {refund.reason && (
                                <div>
                                  <small className="text-muted fst-italic">
                                    {refund.reason}
                                  </small>
                                </div>
                              )}
                              {refund.notes && (
                                <div>
                                  <small className="text-muted">
                                    Notes: {refund.notes}
                                  </small>
                                </div>
                              )}
                            </div>
                            {refund.stripe_refund_id && (
                              <small className="text-muted">
                                {refund.stripe_refund_id}
                              </small>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <CancellationReasonModal
        show={showRefundModal}
        onHide={() => setShowRefundModal(false)}
        handleConfirm={handleRefund}
        loading={isRefunding}
        title="Confirm Refund"
        confirmText="Process Refund"
        staffName={staffName}
      />

      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        loading={isDeleting}
        title="Delete Order"
        handleConfirm={handleDelete}
        body="Are you sure you want to delete this order? This permanently removes the payment record and cannot be undone."
      />
    </>
  );
};

export default DonorGivingDetails;
